
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { History, Bike, Calendar, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { RentalInfo } from '@/hooks/types/rentalTypes';
import { Link } from 'react-router-dom';
import { formatDate } from '@/utils/dateUtils';

interface RentalHistoryCardProps {
  rentals: RentalInfo[];
  loading: boolean;
}

export function RentalHistoryCard({ rentals, loading }: RentalHistoryCardProps) {
  const handleOpenDocument = (documentsUrl: string | undefined) => {
    if (!documentsUrl) {
      return;
    }
    window.open(documentsUrl, '_blank');
  };

  if (loading) {
    return (
      <Card className="w-full bg-card/80 shadow-md">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <History className="h-5 w-5" /> 
            Загрузка истории...
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse flex flex-col space-y-3">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Выводим все аренды для истории, независимо от статуса
  console.log("История аренд. Всего аренд:", rentals.length);
  
  return (
    <Card className="w-full bg-card/80 shadow-md">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <History className="h-5 w-5" /> 
          История аренды
        </CardTitle>
      </CardHeader>
      <CardContent>
        {rentals.length === 0 ? (
          <p className="text-muted-foreground">История аренд пуста</p>
        ) : (
          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-1">
            {rentals.map(rental => {
              // Добавляем отладочные логи для каждой аренды
              console.log(`Аренда ${rental.id}:`, {
                documentsUrl: rental.documentsUrl,
                hasDocumentsUrl: !!rental.documentsUrl
              });
              
              return (
                <div key={rental.id} className="flex justify-between items-center p-2 border-b border-border/30">
                  <div className="flex flex-col">
                    <span className="text-sm font-medium">
                      {rental.human_number ? (
                        <span className="text-primary">{rental.human_number}</span>
                      ) : (
                        `Аренда ${rental.id.substring(0, 8)}`
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Bike className="h-3 w-3" /> {rental.bikeModel || 'Велосипед'}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> {formatDate(rental.startDate)}
                      {rental.endDate && ` - ${formatDate(rental.endDate)}`}
                    </span>
                  </div>
                  <div className="flex flex-col items-end">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge 
                        variant={
                          rental.status === 'completed' ? 'success' : 
                          rental.status === 'cancelled' ? 'destructive' : 
                          rental.status === 'pending' ? 'warning' :
                          rental.status === 'active' ? 'secondary' : 'outline'
                        }
                      >
                        {rental.status === 'completed' ? 'Завершено' : 
                         rental.status === 'cancelled' ? 'Отменено' : 
                         rental.status === 'pending' ? 'Ожидает' :
                         rental.status === 'active' ? 'Активно' : rental.status}
                      </Badge>
                      {rental.documentsUrl && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDocument(rental.documentsUrl)}
                          title="Открыть документ"
                          className="h-6 w-6 p-0"
                        >
                          <ExternalLink className="h-3 w-3" />
                        </Button>
                      )}
                    </div>
                    <span className="text-sm font-medium">{rental.amount.toLocaleString()} ₽</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button asChild variant="outline" className="w-full">
          <Link to="/courier/rentals-history">Посмотреть все</Link>
        </Button>
      </CardFooter>
    </Card>
  );
}
